import type { ReactNode } from 'react';
import { twMerge } from 'tailwind-merge';

import { MIN_PREVIEW_IMAGES } from '../../constants/form';
import {
  formatDimensions,
  getOrientation,
  type ImageDimensions,
  type MediaOrientation,
} from '../../utils/imageDimensions';
import { CharacterCountHint } from '../shared/CharacterCountHint';

interface PreviewUploadsFieldProps {
  dappPreviews: (File | null)[];
  previewDimensions?: ImageDimensions | null;
  existingPreviewUrls?: (string | null)[];
  onAddPreviewSlot: () => void;
  onRemovePreviewSlot: (index: number) => void;
  renderUploadSlot: (index: number) => ReactNode;
}

const EMPTY_PREVIEW_URLS: (string | null)[] = [];

function getPreviewUploadCountLabel(
  dappPreviews: (File | null)[],
  existingPreviewUrls: (string | null)[] = [],
): string {
  const uploadedFiles = dappPreviews.filter(
    (preview) => preview !== null,
  ).length;
  const existingUrls = existingPreviewUrls.filter(
    (url, index) => url !== null && !dappPreviews[index],
  ).length;
  const totalImages = uploadedFiles + existingUrls;

  if (totalImages < MIN_PREVIEW_IMAGES) {
    return `${totalImages} of ${MIN_PREVIEW_IMAGES} minimum images uploaded`;
  }

  return `${totalImages} images uploaded`;
}

export function PreviewUploadsField({
  dappPreviews,
  previewDimensions,
  existingPreviewUrls = EMPTY_PREVIEW_URLS,
  onAddPreviewSlot,
  onRemovePreviewSlot,
  renderUploadSlot,
}: PreviewUploadsFieldProps) {
  const orientation: MediaOrientation = previewDimensions
    ? getOrientation(previewDimensions)
    : 'portrait';

  // match each slot to the image's real ratio so it fills with no letterboxing.
  const slotAspectRatio = previewDimensions
    ? `${previewDimensions.width} / ${previewDimensions.height}`
    : null;

  const gridColsClass =
    orientation === 'landscape'
      ? 'grid-cols-1 sm:grid-cols-2'
      : 'grid-cols-2 sm:grid-cols-4';

  return (
    <div className="self-stretch flex flex-col gap-2">
      <div>
        <span className="text-white text-sm font-bold leading-tight">
          dApp Preview:{' '}
        </span>
        <span className="text-white text-sm font-normal leading-tight">
          {previewDimensions
            ? `All must match your first ${orientation} preview (${formatDimensions(previewDimensions)})`
            : 'Portrait or landscape - all previews must use matching dimensions. Recommended 1080x1920px (portrait) or 1920x1080px (landscape)'}
        </span>
      </div>
      <div className="self-stretch justify-start text-neutral-400 text-sm font-normal leading-tight">
        Images: jpg, png, webp (up to 3MB). Video: mp4 (up to 30MB).
      </div>
      <div className={twMerge('self-stretch grid gap-2', gridColsClass)}>
        {dappPreviews.map((preview, index) => (
          <div
            key={index}
            style={
              slotAspectRatio ? { aspectRatio: slotAspectRatio } : undefined
            }
            className={twMerge(
              'relative group [&_img]:object-contain [&_video]:object-contain',
              !slotAspectRatio && 'aspect-[9/19]',
            )}
          >
            {renderUploadSlot(index)}
            {dappPreviews.length > MIN_PREVIEW_IMAGES &&
              !preview &&
              !existingPreviewUrls[index] && (
                <button
                  type="button"
                  onClick={() => onRemovePreviewSlot(index)}
                  className="group absolute top-1.5 right-1.5 p-1 text-neutral-500 hover:text-white hover:scale-110 active:scale-125 focus:outline-none z-10 pointer-events-auto transition-all duration-200 ease-in-out"
                  aria-label="Remove preview slot"
                >
                  <div className="transition-transform duration-200 ease-in-out group-hover:rotate-90">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      className="size-4"
                      fill="currentColor"
                      viewBox="0 0 24 24"
                      aria-label="Remove"
                    >
                      <title>Remove</title>
                      <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z" />
                    </svg>
                  </div>
                </button>
              )}
          </div>
        ))}
      </div>

      <div className="flex justify-between items-center mt-2">
        <button
          type="button"
          onClick={onAddPreviewSlot}
          className="text-xs text-neutral-500 hover:text-neutral-300 transition-colors flex items-center gap-1"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className="size-3"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            aria-label="Add more"
          >
            <title>Add more</title>
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M12 4v16m8-8H4"
            />
          </svg>
          Add more
        </button>
      </div>

      <CharacterCountHint
        text={getPreviewUploadCountLabel(dappPreviews, existingPreviewUrls)}
      />
    </div>
  );
}
